
import { useState, useEffect } from 'react';
import { latLongToVector3 } from '../utils/globeUtils';

interface LocationData {
  userLocation: [number, number] | null;
  userMarkerPosition: [number, number, number] | null;
  ipAddress: string;
}

export const useLocation = (size: number) => {
  const [locationData, setLocationData] = useState<LocationData>({
    userLocation: null,
    userMarkerPosition: null,
    ipAddress: "Loading..."
  });
  
  // Get user's IP address with geolocation data
  useEffect(() => {
    const fetchIPInfo = async () => {
      try {
        // Try to get IP and geolocation in one request
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        if (data && data.latitude && data.longitude) {
          console.log("User location from IP:", data.latitude, data.longitude, data.ip);
          
          // Use the offset value to elevate the marker above the surface
          const markerPos = latLongToVector3(
            data.latitude, 
            data.longitude, 
            size * 1.05 // Slightly elevated from the surface
          );
          
          setLocationData({
            userLocation: [data.latitude, data.longitude],
            userMarkerPosition: markerPos,
            ipAddress: data.ip || "Unknown"
          });
        } else {
          // Fallback to browser geolocation
          fallbackToGeolocation();
        }
      } catch (error) {
        console.error("Error fetching IP info:", error);
        fallbackToGeolocation();
      }
    };
    
    const fallbackToGeolocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            console.log("User location from browser:", latitude, longitude);
            
            // Use the offset value to elevate the marker above the surface
            const markerPos = latLongToVector3(
              latitude, 
              longitude, 
              size * 1.05 // Slightly elevated from the surface
            );
            
            // Get IP separately
            fetch('https://api.ipify.org?format=json')
              .then(res => res.json())
              .then(data => {
                setLocationData({
                  userLocation: [latitude, longitude],
                  userMarkerPosition: markerPos,
                  ipAddress: data.ip || "Unknown"
                });
              })
              .catch(() => {
                setLocationData({
                  userLocation: [latitude, longitude],
                  userMarkerPosition: markerPos,
                  ipAddress: "Unknown"
                });
              });
          },
          (error) => {
            console.error("Error getting location:", error);
            // Default location if geolocation fails
            const defaultLat = 0;
            const defaultLong = 0;
            const markerPos = latLongToVector3(defaultLat, defaultLong, size * 1.05);
            
            setLocationData(prev => ({
              ...prev,
              userLocation: [defaultLat, defaultLong],
              userMarkerPosition: markerPos
            }));
          },
          { 
            enableHighAccuracy: true, 
            timeout: 10000, 
            maximumAge: 0 
          }
        );
      }
    };
    
    fetchIPInfo();
  }, [size]);
  
  return locationData;
};
